import { DEFAULT_MOVE_SPEED } from '@d2/core';

/**
 * "Walk into range first" — D2's own answer to a click on something that is too
 * far away to act on, held as a value so a Phaser scene never has to.
 *
 * A click on a monster swings at it immediately. If the player was in reach the
 * server takes that swing and this module is finished on the next snapshot. If
 * they were not, the **server's own refusal** is what says so — and the answer
 * to a refusal is to walk toward the body and keep asking until one is taken.
 *
 * ## It is about an *ask*, not about a monster
 *
 * Nothing in here names what is being chased, and that is deliberate rather than
 * incidental: `pursue` reads a sequence, a presence and a clock, and every one
 * of those is true of any intent the server can silently refuse. A **descent**
 * is the second such intent — the room accepts `descend` only from a body close
 * enough to touch the stairs (`descent.room.ts`, `canTouch`, the very reach a
 * pickup is judged by), which is the same "walk there and ask again" shape a
 * swing has. So the same value serves both, and what is being chased — a body's
 * id, or the floor's one exit — is held by the caller that has to walk toward
 * it (`world-view.ts`).
 *
 * It carried a `targetId` until the descent arrived. `pursue` never read it: it
 * was the caller's bookkeeping living in the wrong value, and keeping it would
 * have meant either a fake monster id for the stairs or a second copy of this
 * whole retry machine.
 *
 * ## The client never asks itself whether it is in reach
 *
 * That is the point of doing it this way. Reach, line of sight, hostility,
 * cadence and whether the body is already dead are `@d2/core`'s `admitSwing`,
 * judged by the server against the world it holds; a client that tested any of
 * them to decide when to swing would be a second definition of the rule, and the
 * two would disagree the first time a monster moved between two snapshots.
 *
 * It is also why the walk is ordered on the **refusal** rather than on the
 * click. A click that swings *and* walks would order a body already at arm's
 * length to step into the one it is fighting — and the client predicts walks
 * while the server stops them at contact (`world.ts`: a blocked step is a
 * shorter step), so the two would disagree by more than
 * `CORRECTION_THRESHOLD_CELLS` and the player would watch their character
 * vibrate for as long as the walk lasted. Waiting for the refusal costs one
 * retry interval on a click that was out of reach, and costs a click that was in
 * reach nothing at all.
 *
 * What the client *can* read is the acknowledgement. The room writes
 * `PlayerState.lastSequence` only for an intent it **applied**, so silence is a
 * refusal (`prediction.ts`, `TODO.md` #133) — and because the walk is sent
 * first, it carries the lower number, so an acknowledgement at or above the
 * swing's sequence can only be the swing. Two numbers, and no opinion about
 * combat.
 *
 * That is also why the swing that goes out at the moment of the click is not
 * wasted on a body across the map: `admitSwing` refuses it without drawing from
 * the rng and without spending the attacker's cadence (`ARCHITECTURE.md`:
 * *"nothing is rolled until it is admitted"*), so an out-of-reach ask costs a
 * message and nothing else.
 *
 * ## What a pursuit is not
 *
 * It is not auto-attack. It ends at the **first** swing the server takes, so one
 * click is one blow wherever it was aimed, exactly as one click is one walk. It
 * is not a queue either: a second click replaces it, because the newest thing a
 * player asked for is the thing they want.
 *
 * And it is not a prediction. Nothing here moves the player, nothing here draws,
 * and nothing here decides what a blow does — `ARCHITECTURE.md` forbids
 * predicting a damage number, and a swing has nothing else to predict.
 */

/**
 * How long an unanswered swing waits before it is asked again. **A quarter of a
 * second.**
 *
 * A placeholder, openly, in the sense `INTENT_TIMEOUT_TICKS` is: the honest
 * value is a multiple of the measured round trip, and nothing measures the round
 * trip yet. It is bounded on both sides all the same. It must be *short* against
 * the time a body spends walking the last cell into reach — a cell is crossed in
 * `1000 / DEFAULT_MOVE_SPEED` ms, so a quarter second is one cell and the swing
 * lands within a cell of arriving. And it must be *long* against a room's intent
 * allowance: `descent.room.ts` allows ten a second sustained, so four a second
 * leaves the player's own clicking the majority of the budget.
 */
export const SWING_RETRY_MS = 1000 / DEFAULT_MOVE_SPEED;

/**
 * How long a pursuit chases before giving up. **Five seconds.**
 *
 * Long enough to walk twenty cells at `DEFAULT_MOVE_SPEED`, which is further
 * than the 960×540 canvas can show — so anything a player can actually see and
 * click on is inside it. Short enough that a body which can never be reached
 * (behind rock a monster walked through, on the far side of a refused walk) is
 * eventually let go of rather than asked about until the tab closes.
 *
 * Measured from the click and never from the newest ask, so re-asking cannot
 * push the deadline away.
 */
export const PURSUIT_TIMEOUT_MS = 5000;

/** A click on something, still waiting for the server to do what it asked for. */
export interface Pursuit {
  /** The sequence the newest ask went out under. The one number "was it taken" is asked of. */
  readonly sequence: number;
  /** When that ask went out, on the browser's clock. The retry is measured from here. */
  readonly askedAtMs: number;
  /** When the click happened. The deadline is measured from here, and never moves. */
  readonly beganAtMs: number;
  /**
   * Whether the walk toward the target has been ordered.
   *
   * Once, and only once per pursuit. A body that walks away is therefore not
   * re-chased — the pursuit runs out its deadline and a second click re-aims —
   * which is the smallest thing that works and is a real narrowing, stated here
   * rather than discovered: monsters in this game close on the player rather
   * than flee (`monster.ts`), so the target of a pursuit is coming *towards* the
   * one thing a re-path would be for. A staircase does not move at all, which
   * makes one walk exactly right for it.
   */
  readonly approached: boolean;
}

/** What a pursuit wants done this frame. */
export type PursuitStep =
  /** Nothing. The answer could still be in flight. */
  | 'wait'
  /** Walk toward the target, then ask again. The answer to the first refusal. */
  | 'approach'
  /** Ask again: the same intent at the same target, under a fresh sequence. */
  | 'ask'
  /** Over — taken, gone, or given up on. The caller forgets it. */
  | 'done';

/** What the frame knows that a pursuit needs. */
export interface PursuitReading {
  /** The newest sequence the server has said it applied (`Prediction.acknowledged`). */
  readonly acknowledged: number;
  /**
   * Whether the thing being chased is still there at all.
   *
   * For a body, whether the room is still replicating it — a corpse is not worth
   * asking about. A floor's way down cannot go anywhere, so a descent's answer
   * is simply always `true` and its only ending is the acknowledgement or the
   * deadline.
   */
  readonly present: boolean;
  /** The browser's clock, the same reading the rest of the frame is drawn at. */
  readonly nowMs: number;
}

/** A click: the first ask has gone out, and this is what is left to do about it. */
export const beginPursuit = (sequence: number, nowMs: number): Pursuit => ({
  sequence,
  askedAtMs: nowMs,
  beganAtMs: nowMs,
  approached: false,
});

/**
 * The same pursuit, one ask later.
 *
 * The deadline is deliberately carried rather than reset — asking again must not
 * push away the moment this gives up — and the approach is marked done, because
 * the ask that follows an `'approach'` is the one that comes with the walk.
 */
export const askedAgain = (pursuit: Pursuit, sequence: number, nowMs: number): Pursuit => ({
  ...pursuit,
  sequence,
  askedAtMs: nowMs,
  approached: true,
});

/**
 * What to do about a pursuit this frame.
 *
 * The order of the three endings matters. **Acknowledged first**, because a
 * swing the server took is a finished pursuit even on the frame the body stops
 * being replicated — which is exactly what a killing blow looks like from here.
 * **Gone second**, so nothing keeps asking about a corpse. **Timed out third**,
 * which is the only ending that is a give-up rather than an outcome.
 */
export const pursue = (pursuit: Pursuit, { acknowledged, present, nowMs }: PursuitReading): PursuitStep => {
  if (acknowledged >= pursuit.sequence) return 'done';
  if (!present) return 'done';
  if (nowMs - pursuit.beganAtMs >= PURSUIT_TIMEOUT_MS) return 'done';
  if (nowMs - pursuit.askedAtMs < SWING_RETRY_MS) return 'wait';

  return pursuit.approached ? 'ask' : 'approach';
};
